import { useState, useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useBreadcrumb, BreadcrumbItem } from "@/contexts/BreadcrumbContext";
import { ProductEditNoVariants } from "@/routes/ProductEditNoVariants";
import { ProductEditWithVariants } from "@/routes/ProductEditWithVariants";
import type { ProductEditPageProduct } from "@/routes/productEditTypes";
import {
  getAvailableProductEditTabs,
  isProductEditTab,
  productEditTabPath,
} from "@/routes/productEditTabs";

function productUsesVariantEditor(product: ProductEditPageProduct): boolean {
  const hasVariants = (product.variants?.length ?? 0) > 0;
  return hasVariants || product.version === 1;
}

export function ProductEdit() {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const [product, setProduct] = useState<ProductEditPageProduct | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasShopify, setHasShopify] = useState(false);
  const [hasKunaki, setHasKunaki] = useState(false);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const fetchProduct = async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!id) return;

    if (!silent) {
      setIsLoading(true);
    }

    try {
      const response = await fetch(`/api/products/${id}`);
      if (response.ok) {
        const data = await response.json();
        const productData = data.product as ProductEditPageProduct;
        setProduct(productData);

        const productName = productData.name || "Product";
        const breadcrumbs: BreadcrumbItem[] = [
          { label: "Sales", path: "/products" },
          { label: "Products", path: "/products" },
          { label: productName, path: productEditTabPath({ productId: id, tab: "product" }) },
        ];
        setBreadcrumbs(breadcrumbs);
      } else {
        console.error("Failed to fetch product");
      }
    } catch (error) {
      console.error("Failed to fetch product:", error);
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    async function loadProduct() {
      setIsLoading(true);
      await fetchProduct();
    }

    async function fetchIntegrations() {
      try {
        const shopifyResponse = await fetch("/api/shopify");
        if (shopifyResponse.ok) {
          const shopifyData = await shopifyResponse.json();
          if (shopifyData.shopify) {
            setHasShopify(true);
          }
        } else if (shopifyResponse.status !== 404) {
          console.error("Failed to fetch Shopify integration");
        }

        const kunakiResponse = await fetch("/api/kunaki");
        if (kunakiResponse.ok) {
          const kunakiData = await kunakiResponse.json();
          if (kunakiData.kunaki) {
            setHasKunaki(true);
          }
        } else if (kunakiResponse.status !== 404) {
          console.error("Failed to fetch Kunaki integration");
        }
      } catch (error) {
        console.error("Failed to fetch integrations:", error);
      }
    }

    loadProduct();
    fetchIntegrations();

    return () => {
      clearBreadcrumbs();
    };
  }, [id]);

  if (!id) {
    return <div className="p-4">Missing product id</div>;
  }

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  if (!product) {
    return <div className="p-4">Product not found</div>;
  }

  if (!tab) {
    return (
      <Navigate
        to={productEditTabPath({ productId: id, tab: "product" })}
        replace
      />
    );
  }

  const availableTabs = getAvailableProductEditTabs({
    productType: product.type,
    hasShopify,
    hasKunaki,
  });

  if (!isProductEditTab(tab) || !availableTabs.includes(tab)) {
    return (
      <Navigate
        to={productEditTabPath({ productId: id, tab: "product" })}
        replace
      />
    );
  }

  const variantEditor = productUsesVariantEditor(product);

  if (variantEditor) {
    return (
      <ProductEditWithVariants
        productId={id}
        product={product}
        activeTab={tab}
        hasShopify={hasShopify}
        hasKunaki={hasKunaki}
        onFlowChange={() => void fetchProduct({ silent: true })}
      />
    );
  }

  return (
    <ProductEditNoVariants
      productId={id}
      product={product}
      activeTab={tab}
      hasShopify={hasShopify}
      hasKunaki={hasKunaki}
      onFlowChange={() => void fetchProduct({ silent: true })}
    />
  );
}
